import { RefObject, useEffect, useRef, useState } from 'react';
import aiApi from '../../../api/ai.api';
import RemoteResultHelper from '../../../helper/RemoteResultHelper';
import { RemoteResult } from '../../common/RemoteResultCommand/index.types';
import { captureFrame, grayscaleFrame, meanAbsDiff, visibleViewport, GrayFrame } from '../helper/frameCapture';
import { mapFastDetectionToRemoteResult } from '../helper/fastDetectionMapping';
import { AI_FAST_DETECT_TIMEOUT_MS, AI_LIVE_FRAME_JPEG_QUALITY, AI_LIVE_FRAME_MAX_DIMENSION, GRAY_SAMPLE_SIZE } from '../helper/liveConstants';
import { DetectionSource } from '../helper/trackerSettings';
import { DetectionPhase, fastDetectDelayMs } from '../helper/detectionPhase';

export type LiveDetectionStatus = 'idle' | 'polling' | 'offline';
export type Connectivity = 'online' | 'offline';

// A single failed request is not enough to declare offline — a brief hiccup or one dropped frame
// happens on mobile networks. Only this many CONSECUTIVE network failures flips to offline, which
// then surfaces the offline panel (see AiReadingScreen). A successful response resets the count.
export const CONSECUTIVE_FAILURES_OFFLINE = 2;

interface UseLiveDetectionOptions {
    videoRef: RefObject<HTMLVideoElement>;
    enabled: boolean;
    getToken: () => Promise<string>;
    config: { pollIntervalMs: number; liveTimeoutMs: number; frameDiffThreshold: number };
    // Which endpoint feeds the loop: 'full' calls evaluateImage (boxes+values); 'fast' calls the
    // cheaper detect/fast endpoint (boxes only). In 'fast' mode the inter-request delay is adaptive
    // (see getPhase / fastDetectDelayMs); in 'full' mode it stays config.pollIntervalMs.
    source: DetectionSource;
    // Current detection phase, read fresh at every tick (via a ref) so the fast loop can adapt its
    // cadence without re-subscribing the effect. Ignored while source === 'full'.
    getPhase: () => DetectionPhase;
    // capturedAt: performance.now() of the moment the frame was grabbed — by the time the result
    // arrives it is 1-2s stale, and the box tracker uses this to motion-compensate the boxes.
    onResult: (result: RemoteResult | undefined, frameBase64: string, capturedAt: number) => void;
    // Fired on connectivity TRANSITIONS only (not on every request): 'offline' once
    // CONSECUTIVE_FAILURES_OFFLINE network failures pile up, 'online' on the first success after
    // that. The screen maps these onto the WENT_OFFLINE / BACK_ONLINE machine events.
    onConnectivityChange?: (connectivity: Connectivity) => void;
}

export const useLiveDetection = ({ videoRef, enabled, getToken, config, source, getPhase, onResult, onConnectivityChange }: UseLiveDetectionOptions): { status: LiveDetectionStatus } => {
    const [status, setStatus] = useState<LiveDetectionStatus>('idle');

    const lastSampleRef = useRef<GrayFrame | null>(null);
    const hadResponseRef = useRef(false);
    const consecutiveFailuresRef = useRef(0);
    const offlineRef = useRef(false);
    const onResultRef = useRef(onResult);
    onResultRef.current = onResult;
    const onConnectivityChangeRef = useRef(onConnectivityChange);
    onConnectivityChangeRef.current = onConnectivityChange;
    const getTokenRef = useRef(getToken);
    getTokenRef.current = getToken;
    const getPhaseRef = useRef(getPhase);
    getPhaseRef.current = getPhase;

    useEffect(() => {
        if (!enabled) {
            setStatus('idle');
            return undefined;
        }

        setStatus('polling');
        // The screen pauses the loop while the offline panel is shown and restarts it on "retry";
        // every (re)start begins with a clean connectivity state, otherwise a connection that is
        // still down could never raise the offline transition again (#54).
        consecutiveFailuresRef.current = 0;
        offlineRef.current = false;
        let disposed = false;
        let timerId = 0;
        let activeController: AbortController | null = null;

        // Per-request abort timeout (unchanged): 'fast' uses its own shorter constant.
        const liveTimeoutMs = source === 'fast' ? AI_FAST_DETECT_TIMEOUT_MS : config.liveTimeoutMs;

        // Delay until the NEXT tick, measured from the end of the current one. Adaptive in 'fast'
        // mode (phase-dependent), the configured poll interval in 'full' mode.
        const nextDelay = (): number => (source === 'fast' ? fastDetectDelayMs(getPhaseRef.current()) : config.pollIntervalMs);

        const scheduleNext = () => {
            if (disposed) {
                return;
            }
            timerId = window.setTimeout(tick, nextDelay());
        };

        const tick = async () => {
            if (disposed) {
                return;
            }
            const video = videoRef.current;
            if (!video) {
                scheduleNext();
                return;
            }

            // Everything is captured cropped to the visible (object-fit: cover) viewport.
            const viewport = visibleViewport();
            const sample = grayscaleFrame(video, GRAY_SAMPLE_SIZE, viewport);
            if (sample && lastSampleRef.current && hadResponseRef.current) {
                if (meanAbsDiff(sample, lastSampleRef.current) < config.frameDiffThreshold) {
                    lastSampleRef.current = sample;
                    scheduleNext();
                    return;
                }
            }
            if (sample) {
                lastSampleRef.current = sample;
            }

            const frame = captureFrame(video, AI_LIVE_FRAME_MAX_DIMENSION, AI_LIVE_FRAME_JPEG_QUALITY, viewport);
            if (!frame) {
                scheduleNext();
                return;
            }
            const capturedAt = performance.now();

            const controller = new AbortController();
            activeController = controller;
            const timeoutId = window.setTimeout(() => controller.abort(), liveTimeoutMs);

            let token: string;
            try {
                token = await getTokenRef.current();
            } catch (err) {
                // Token fetch failed (could be auth-related) — end the tick silently and keep polling.
                window.clearTimeout(timeoutId);
                activeController = null;
                scheduleNext();
                return;
            }

            try {
                let result: RemoteResult | undefined;
                if (source === 'fast') {
                    const res = await aiApi.detectFast(controller, token, { image: frame });
                    if (disposed) {
                        return;
                    }
                    result = mapFastDetectionToRemoteResult(res.data);
                } else {
                    const res = await aiApi.evaluateImage(controller, token, { image: frame });
                    if (disposed) {
                        return;
                    }
                    // allowEmptyRates: during framing a meter/display box without a readable value is
                    // still a valid "meter visible" signal and feeds the overlay box.
                    result = RemoteResultHelper.mapImageEvaluationResponseToRemoteResult(res.data, { allowEmptyRates: true });
                }
                // A successful response proves connectivity: reset the failure streak and, if we
                // had declared offline, announce recovery.
                consecutiveFailuresRef.current = 0;
                if (offlineRef.current) {
                    offlineRef.current = false;
                    onConnectivityChangeRef.current?.('online');
                }
                hadResponseRef.current = true;
                setStatus('polling');
                onResultRef.current(result, frame, capturedAt);
            } catch (err: any) {
                if (disposed) {
                    return;
                }
                // While the loop runs, only our own request timeout aborts the controller (the
                // cleanup abort is filtered out by `disposed` above) — so an abort here means the
                // request hung. A hanging connection (dead spot, WLAN without internet) never
                // produces a network error, it only times out, and must count as offline too (#54).
                const isAbort = err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';
                const isNetworkError = isAbort || !err?.response;
                const offlineSignal = (typeof navigator !== 'undefined' && navigator.onLine === false) || isNetworkError;
                if (offlineSignal) {
                    consecutiveFailuresRef.current += 1;
                    // Only flip (and notify) once, on the transition — repeated failures stay offline.
                    if (consecutiveFailuresRef.current >= CONSECUTIVE_FAILURES_OFFLINE && !offlineRef.current) {
                        offlineRef.current = true;
                        setStatus('offline');
                        onConnectivityChangeRef.current?.('offline');
                    }
                }
            } finally {
                window.clearTimeout(timeoutId);
                activeController = null;
            }
            scheduleNext();
        };

        scheduleNext();

        return () => {
            disposed = true;
            window.clearTimeout(timerId);
            activeController?.abort();
        };
    }, [enabled, videoRef, config.pollIntervalMs, config.liveTimeoutMs, config.frameDiffThreshold, source]);

    return { status };
};
